import React, { useMemo, useState } from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Button,
  Box,
  Card,
  CardBody,
  CardHeader,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useClipboard,
  useColorModeValue,
} from '@chakra-ui/react'
import {
  FiChevronDown,
  FiCopy,
  FiExternalLink,
  FiEye,
  FiMoreVertical,
  FiShield,
} from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { getChainByChainId, getChainByAlias } from '@shared/chainRegistryClient'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { formatChainName } from '@/constants/chains'
import { monitoringService } from '@/services/admin/monitoringService'
import type { FeeTreasuryOverview, PlatformContractDescriptor } from '@/types'
import { TableSkeleton } from '@/components/common/LoadingSkeleton'
import { ChainBadge } from '@/components/common/ChainBadge'
import { getExplorerAddressUrl, truncateAddress } from '@/utils/formatUtils'
import { FeeTreasuryDetailsModal } from '@/components/feeTreasury/FeeTreasuryDetailsModal'
import { FactoryDetailsModal } from '@/components/monitoring/FactoryDetailsModal'
import { ProgramDetailsModal } from '@/components/monitoring/ProgramDetailsModal'
import { PlatformContractConsistencyModal } from '@/components/monitoring/PlatformContractConsistencyModal'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'

type NetworkBucket = 'mainnet' | 'testnet'

interface MultiSelectOption {
  value: string
  label: string
}

const formatContractType = (value: string): string => {
  return value
    .toLowerCase()
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

const resolveChainName = (contract: PlatformContractDescriptor): string => {
  const formatted = formatChainName(contract.chainId, 'full')
  const normalizedFormatted = formatted.trim().toLowerCase()
  if (formatted && normalizedFormatted !== 'unknown' && normalizedFormatted !== 'unknown chain') {
    return formatted
  }

  const chain =
    getChainByChainId(Number(contract.chainId)) ??
    getChainByAlias(contract.chainKey) ??
    getChainByAlias(contract.chainCode)
  return chain?.name ?? contract.chainKey
}

const sortContracts = (contracts: PlatformContractDescriptor[]): PlatformContractDescriptor[] => {
  return contracts
    .slice()
    .sort((a, b) => {
      const chainA = resolveChainName(a).toLowerCase()
      const chainB = resolveChainName(b).toLowerCase()
      if (chainA !== chainB) {
        return chainA.localeCompare(chainB)
      }
      return a.contractType.localeCompare(b.contractType)
    })
}

interface MultiSelectFilterProps {
  value: string[]
  options: MultiSelectOption[]
  onChange: (value: string[]) => void
  placeholder: string
  size?: 'sm' | 'md'
  maxMenuHeight?: string | number
}

const MultiSelectFilter = ({
  value,
  options,
  onChange,
  placeholder,
  size = 'sm',
  maxMenuHeight = '240px',
}: MultiSelectFilterProps) => {
  const borderColor = useColorModeValue('gray.300', 'gray.600')
  const hoverBorderColor = useColorModeValue('blue.400', 'blue.300')
  const openBorderColor = useColorModeValue('blue.500', 'blue.300')
  const buttonBg = useColorModeValue('white', 'gray.800')
  const openBg = useColorModeValue('blue.50', 'blue.900')
  const menuBg = useColorModeValue('white', 'gray.800')
  const optionHoverBg = useColorModeValue('gray.100', 'whiteAlpha.100')
  const optionCheckedBg = useColorModeValue('blue.50', 'blue.900')
  const optionCheckedColor = useColorModeValue('blue.700', 'blue.100')
  const iconColor = useColorModeValue('blue.600', 'blue.200')
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400')
  const optionFontSize = size === 'sm' ? 'sm' : 'md'
  const optionPaddingY = size === 'sm' ? 2 : 2.5

  const selectedOptions = options.filter(option => value.includes(option.value))
  const computedLabel = selectedOptions.length === 0
    ? placeholder
    : selectedOptions.length <= 2
      ? selectedOptions.map(option => option.label).join(', ')
      : `${selectedOptions.length} selected`

  return (
    <Menu isLazy closeOnSelect={false} matchWidth>
      <MenuButton
        as={Button}
        variant="unstyled"
        size={size}
        w="100%"
        h={size === 'sm' ? '40px' : '48px'}
        px={size === 'sm' ? 3 : 4}
        borderRadius="xl"
        borderWidth="1px"
        borderColor={borderColor}
        bg={buttonBg}
        justifyContent="space-between"
        fontWeight="normal"
        lineHeight="1.2"
        _hover={{
          borderColor: hoverBorderColor,
          bg: buttonBg,
        }}
        _expanded={{
          borderColor: openBorderColor,
          bg: openBg,
          boxShadow: `0 0 0 1px ${openBorderColor}`,
        }}
        _focusVisible={{
          borderColor: openBorderColor,
          boxShadow: `0 0 0 2px ${openBorderColor}`,
        }}
        _focus={{
          borderColor: openBorderColor,
          boxShadow: 'none',
        }}
      >
        <HStack w="100%" justify="space-between" spacing={3} align="center">
          <Text
            flex="1"
            textAlign="left"
            noOfLines={1}
            color={selectedOptions.length > 0 ? 'inherit' : mutedTextColor}
            fontSize={optionFontSize}
          >
            {computedLabel}
          </Text>
          <Icon as={FiChevronDown} color={iconColor} boxSize={5} flexShrink={0} />
        </HStack>
      </MenuButton>
      <MenuList
        p={1.5}
        borderRadius="xl"
        borderColor={borderColor}
        bg={menuBg}
        boxShadow="lg"
        maxH={maxMenuHeight}
        overflowY="auto"
        minW="100%"
      >
        <MenuOptionGroup
          type="checkbox"
          value={value}
          onChange={optionValue =>
            onChange(Array.isArray(optionValue) ? optionValue.map(String) : [String(optionValue)])
          }
        >
          {options.map((option, index) => (
            <MenuItemOption
              key={`${option.value}-${index}`}
              value={option.value}
              borderRadius="md"
              px={3}
              py={optionPaddingY}
              fontSize={optionFontSize}
              _hover={{ bg: optionHoverBg }}
              _focus={{ bg: optionHoverBg }}
              _checked={{
                bg: optionCheckedBg,
                color: optionCheckedColor,
                fontWeight: 'semibold',
              }}
            >
              {option.label}
            </MenuItemOption>
          ))}
        </MenuOptionGroup>
      </MenuList>
    </Menu>
  )
}

interface AddressCellProps {
  address: string
  chainId: number
}

const AddressCell = ({ address, chainId }: AddressCellProps) => {
  const { t } = useTranslation()
  const { hasCopied, onCopy } = useClipboard(address)
  const explorerUrl = useMemo(
    () => getExplorerAddressUrl(chainId, address),
    [chainId, address]
  )

  return (
    <HStack spacing={1} flexWrap="wrap">
      <Tooltip label={address} openDelay={300}>
        <Text fontFamily="mono" fontSize="xs">
          {truncateAddress(address)}
        </Text>
      </Tooltip>
      <Tooltip label={hasCopied ? t('common.copySuccess') : t('common.copy')}>
        <IconButton
          aria-label="copy-contract-address"
          icon={<FiCopy />}
          size="xs"
          variant="ghost"
          colorScheme={hasCopied ? 'green' : 'gray'}
          onClick={onCopy}
        />
      </Tooltip>
      {explorerUrl && (
        <Tooltip label={t('common.openExplorer')}>
          <IconButton
            aria-label="open-contract-explorer"
            icon={<FiExternalLink />}
            size="xs"
            variant="ghost"
            as="a"
            href={explorerUrl}
            target="_blank"
            rel="noopener noreferrer"
          />
        </Tooltip>
      )}
    </HStack>
  )
}

export const PlatformContractsMonitor: React.FC = () => {
  const { t } = useTranslation()
  const { currentEnv } = useNetworkEnvironment()
  const cardBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const [selectedFeeTreasury, setSelectedFeeTreasury] = useState<FeeTreasuryOverview | null>(null)
  const [isFactoryOpen, setIsFactoryOpen] = useState(false)
  const [selectedFactory, setSelectedFactory] = useState<PlatformContractDescriptor | null>(null)
  const [isProgramOpen, setIsProgramOpen] = useState(false)
  const [selectedProgram, setSelectedProgram] = useState<PlatformContractDescriptor | null>(null)
  const [isConsistencyOpen, setIsConsistencyOpen] = useState(false)
  const [selectedConsistencyContract, setSelectedConsistencyContract] =
    useState<PlatformContractDescriptor | null>(null)
  const [chainFilter, setChainFilter] = useState<string[]>([])
  const [contractFilter, setContractFilter] = useState<string[]>([])
  const [versionFilter, setVersionFilter] = useState<string[]>([])
  const [statusFilter, setStatusFilter] = useState<string[]>([])

  const {
    data: contracts = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['monitoring', 'platform-contracts', currentEnv],
    queryFn: () => monitoringService.getPlatformContracts(currentEnv),
    refetchOnWindowFocus: false,
  })

  const grouped = useMemo(() => {
    const buckets: Record<NetworkBucket, PlatformContractDescriptor[]> = {
      mainnet: [],
      testnet: [],
    }

    contracts.forEach(contract => {
      const bucket: NetworkBucket = contract.isTestnet ? 'testnet' : 'mainnet'
      buckets[bucket].push(contract)
    })

    return {
      mainnet: sortContracts(buckets.mainnet),
      testnet: sortContracts(buckets.testnet),
    }
  }, [contracts])

  const activeNetwork: NetworkBucket = currentEnv === 'testnet' ? 'testnet' : 'mainnet'
  const activeItems = grouped[activeNetwork]

  const chainOptions = useMemo(() => {
    const entries = Array.from(new Set(activeItems.map(contract => resolveChainName(contract))))
      .sort((a, b) => a.localeCompare(b))
    return entries.map(value => ({ value, label: value }))
  }, [activeItems])

  const contractOptions = useMemo(() => {
    const entries = Array.from(new Set(activeItems.map(contract => contract.contractType)))
      .sort((a, b) => a.localeCompare(b))
    return entries.map(value => ({ value, label: formatContractType(value) }))
  }, [activeItems])

  const versionOptions = useMemo(() => {
    const entries = Array.from(new Set(activeItems.map(contract => contract.version)))
      .sort((a, b) => a.localeCompare(b))
    return entries.map(value => ({ value, label: value }))
  }, [activeItems])

  const statusOptions = useMemo(() => ([
    { value: 'active', label: t('monitoring.platformContracts.labels.active') },
    { value: 'inactive', label: t('monitoring.platformContracts.labels.inactive') },
  ]), [t])

  const filteredItems = useMemo(() => {
    return activeItems.filter(contract => {
      const matchesChain = chainFilter.length === 0
        || chainFilter.includes(resolveChainName(contract))
      const matchesContract = contractFilter.length === 0
        || contractFilter.includes(contract.contractType)
      const matchesVersion = versionFilter.length === 0
        || versionFilter.includes(contract.version)
      const matchesStatus = statusFilter.length === 0
        || statusFilter.includes(contract.isActive ? 'active' : 'inactive')
      return matchesChain && matchesContract && matchesVersion && matchesStatus
    })
  }, [activeItems, chainFilter, contractFilter, versionFilter, statusFilter])

  const openFeeTreasuryDetails = (contract: PlatformContractDescriptor) => {
    setSelectedFeeTreasury({
      id: contract.id,
      chainRegistryId: contract.chainRegistryId,
      chainId: contract.chainId,
      chainKey: contract.chainKey,
      chainCode: contract.chainCode,
      chainName: resolveChainName(contract),
      compatibility: contract.compatibility,
      contractAddress: contract.contractAddress,
      contractName: contract.name?.trim() || formatContractType(contract.contractType),
      version: contract.version,
      isActive: contract.isActive,
      isTestnet: contract.isTestnet,
      metadata: contract.metadata ?? null,
    })
    setIsDetailsOpen(true)
  }

  const closeFeeTreasuryDetails = () => {
    setIsDetailsOpen(false)
    setSelectedFeeTreasury(null)
  }

  const openFactoryDetails = (contract: PlatformContractDescriptor) => {
    setSelectedFactory(contract)
    setIsFactoryOpen(true)
  }

  const closeFactoryDetails = () => {
    setIsFactoryOpen(false)
    setSelectedFactory(null)
  }

  const openProgramDetails = (contract: PlatformContractDescriptor) => {
    setSelectedProgram(contract)
    setIsProgramOpen(true)
  }

  const closeProgramDetails = () => {
    setIsProgramOpen(false)
    setSelectedProgram(null)
  }

  const openConsistencyCheck = (contract: PlatformContractDescriptor) => {
    setSelectedConsistencyContract(contract)
    setIsConsistencyOpen(true)
  }

  const closeConsistencyCheck = () => {
    setIsConsistencyOpen(false)
    setSelectedConsistencyContract(null)
  }

  const renderTable = (items: PlatformContractDescriptor[]) => {
    if (isLoading) {
      return <TableSkeleton rows={6} />
    }

    if (items.length === 0) {
      return (
        <Text color="gray.500" fontSize="sm">
          {t('monitoring.platformContracts.labels.empty')}
        </Text>
      )
    }

    return (
      <Box overflowX="auto" w="100%">
        <Table
          w="100%"
          size="sm"
          variant="simple"
          sx={{ th: { px: 1.5, py: 1.5 }, td: { px: 1.5, py: 1.5 } }}
        >
          <Thead>
            <Tr>
              <Th>{t('monitoring.platformContracts.labels.chain')}</Th>
              <Th>{t('monitoring.platformContracts.labels.contract')}</Th>
              <Th>{t('monitoring.platformContracts.labels.address')}</Th>
              <Th>{t('monitoring.platformContracts.labels.version')}</Th>
              <Th>{t('monitoring.platformContracts.labels.status')}</Th>
              <Th textAlign="right">{t('monitoring.platformContracts.labels.actions')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {items.map(contract => {
              const chainName = resolveChainName(contract)
              const typeLabel = formatContractType(contract.contractType)
              const isFeeTreasury = contract.contractType === 'FEE_TREASURY'
              const isFactory = contract.contractType.toUpperCase().includes('FACTORY')
              const isProgram = contract.contractType === 'PROGRAM'
              const canViewDetails = isFeeTreasury || isFactory || isProgram
              const canCheckConsistency = activeNetwork === 'testnet'
                && contract.isTestnet
                && contract.contractType === 'FAUCET_BATCH_MINTER'
              return (
                <Tr key={contract.id}>
                  <Td>
                    <ChainBadge
                      chainId={contract.chainId}
                      chainCode={contract.chainCode}
                      chainName={chainName}
                      chainNumericId={contract.chainId}
                      isTestnet={contract.isTestnet}
                    />
                  </Td>
                  <Td>
                    <Text fontWeight="medium">{typeLabel}</Text>
                  </Td>
                  <Td maxW="190px">
                    <AddressCell
                      address={contract.contractAddress}
                      chainId={contract.chainId}
                    />
                  </Td>
                  <Td>
                    <Text fontFamily="mono" fontSize="xs">
                      {contract.version}
                    </Text>
                  </Td>
                  <Td>
                    <Badge colorScheme={contract.isActive ? 'green' : 'yellow'}>
                      {contract.isActive
                        ? t('monitoring.platformContracts.labels.active')
                        : t('monitoring.platformContracts.labels.inactive')}
                    </Badge>
                  </Td>
                  <Td textAlign="right">
                    <Menu placement="bottom-end" isLazy>
                      <Tooltip label={t('monitoring.platformContracts.labels.actions')}>
                        <MenuButton
                          as={IconButton}
                          aria-label={t('monitoring.platformContracts.labels.actions')}
                          icon={<FiMoreVertical />}
                          size="xs"
                          variant="ghost"
                        />
                      </Tooltip>
                      <MenuList minW="180px">
                        {canViewDetails && (
                          <MenuItem
                            icon={<FiEye />}
                            onClick={() =>
                              isFeeTreasury
                                ? openFeeTreasuryDetails(contract)
                                : isFactory
                                  ? openFactoryDetails(contract)
                                  : openProgramDetails(contract)
                            }
                          >
                            {t('monitoring.platformContracts.labels.viewDetails')}
                          </MenuItem>
                        )}
                        {canCheckConsistency && (
                          <MenuItem
                            icon={<FiShield />}
                            onClick={() => openConsistencyCheck(contract)}
                          >
                            {t(
                              'monitoring.platformContracts.consistency.open',
                              'Consistency'
                            )}
                          </MenuItem>
                        )}
                        {!canViewDetails && !canCheckConsistency && (
                          <MenuItem isDisabled>
                            {t('monitoring.platformContracts.labels.noActions', 'No actions')}
                          </MenuItem>
                        )}
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              )
            })}
          </Tbody>
        </Table>
      </Box>
    )
  }

  return (
    <VStack align="stretch" spacing={4}>
      <Box>
        <Heading size="md" mb={1}>
          {t('monitoring.platformContracts.title')}
        </Heading>
      </Box>

      {error && (
        <Alert status="error">
          <AlertIcon />
          <Box>
            <AlertTitle>{t('common.error')}</AlertTitle>
            <AlertDescription>
              {error instanceof Error
                ? error.message
                : t('monitoring.platformContracts.labels.error')}
            </AlertDescription>
          </Box>
        </Alert>
      )}

      <Box w="100%" maxW="100%">
        {[activeNetwork].map((network) => {
          const networkLabel =
            network === 'mainnet'
              ? t('monitoring.platformContracts.mainnet')
              : t('monitoring.platformContracts.testnet')
          const colorScheme = network === 'mainnet' ? 'green' : 'purple'
          const items = filteredItems

          return (
            <Card key={network} w="100%" bg={cardBg} borderWidth="1px" borderColor={borderColor}>
              <CardHeader py={2}>
                <HStack justify="space-between">
                  <HStack spacing={3}>
                    <Heading size="sm">{networkLabel}</Heading>
                    <Badge colorScheme={colorScheme}>{items.length}</Badge>
                  </HStack>
                  <RefreshIconButton
                    label={t('common.refresh')}
                    onClick={() => refetch()}
                    isLoading={isFetching}
                  />
                </HStack>
              </CardHeader>
              <CardBody pt={0}>
                <Box mb={4}>
                  <HStack spacing={3} align="end" flexWrap="wrap">
                    <FormControl maxW={{ base: 'full', md: '220px' }}>
                      <FormLabel fontSize="sm" mb={1}>Chain</FormLabel>
                      <MultiSelectFilter
                        size="sm"
                        value={chainFilter}
                        options={chainOptions}
                        onChange={setChainFilter}
                        placeholder="All chains"
                      />
                    </FormControl>
                    <FormControl maxW={{ base: 'full', md: '220px' }}>
                      <FormLabel fontSize="sm" mb={1}>Contract</FormLabel>
                      <MultiSelectFilter
                        size="sm"
                        value={contractFilter}
                        options={contractOptions}
                        onChange={setContractFilter}
                        placeholder="All contracts"
                      />
                    </FormControl>
                    <FormControl maxW={{ base: 'full', md: '180px' }}>
                      <FormLabel fontSize="sm" mb={1}>Version</FormLabel>
                      <MultiSelectFilter
                        size="sm"
                        value={versionFilter}
                        options={versionOptions}
                        onChange={setVersionFilter}
                        placeholder="All versions"
                      />
                    </FormControl>
                    <FormControl maxW={{ base: 'full', md: '180px' }}>
                      <FormLabel fontSize="sm" mb={1}>Status</FormLabel>
                      <MultiSelectFilter
                        size="sm"
                        value={statusFilter}
                        options={statusOptions}
                        onChange={setStatusFilter}
                        placeholder="All statuses"
                      />
                    </FormControl>
                  </HStack>
                </Box>
                {renderTable(items)}
              </CardBody>
            </Card>
          )
        })}
      </Box>

      <FeeTreasuryDetailsModal
        isOpen={isDetailsOpen}
        onClose={closeFeeTreasuryDetails}
        overview={isDetailsOpen ? selectedFeeTreasury : null}
      />
      <FactoryDetailsModal
        isOpen={isFactoryOpen}
        onClose={closeFactoryDetails}
        contract={isFactoryOpen ? selectedFactory : null}
        mode={currentEnv}
      />
      <ProgramDetailsModal
        isOpen={isProgramOpen}
        onClose={closeProgramDetails}
        contract={isProgramOpen ? selectedProgram : null}
        mode={currentEnv}
      />
      <PlatformContractConsistencyModal
        isOpen={isConsistencyOpen}
        onClose={closeConsistencyCheck}
        mode={currentEnv}
        targetContract={selectedConsistencyContract}
      />
    </VStack>
  )
}

export default PlatformContractsMonitor
